import { lazy, Suspense, useLayoutEffect, useRef, useState } from "react";
import type { ComponentType, LazyExoticComponent } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  matchRoutes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ToastProvider } from "./contexts/ToastContext";
import { ProtectedRoute, FeatureRoute } from "./utils/roleGuard";
import ErrorBoundary from "./components/ErrorBoundary";
import { ADMIN_ROLES, ANALYST_ROLES } from "./constants/roles";
import type { RoleName } from "./constants/roles";
import Sidebar from "./components/Layout/Sidebar";
import TopBar from "./components/Layout/TopBar";
import OnboardingWizard from "./components/Onboarding/OnboardingWizard";
import TwoFAGraceNotice from "./components/TwoFAGraceNotice";
import { LoadingText } from "./components/ui";
import { useAuth } from "./hooks/useAuth";
import { getGreeting } from "./utils";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DemoLandingPage from "./pages/DemoLandingPage";
import NotFoundPage from "./pages/NotFoundPage";

const IS_DEMO = import.meta.env.VITE_APP_ENV === "demo";
const HOME_PATH = "/dashboard";

type PageModule = { default: ComponentType };
type LazyPage = LazyExoticComponent<ComponentType> & { preload: () => Promise<PageModule> };

interface AppRoute {
  path: string;
  title: string;
  Page: LazyPage;
  feature?: string;
  roles?: RoleName[];
  showGreeting?: boolean;
}

function lazyPage(load: () => Promise<PageModule>): LazyPage {
  return Object.assign(lazy(load), { preload: load });
}

const APP_ROUTES: AppRoute[] = [
  { path: "/chat", title: "Chat", feature: "chat", Page: lazyPage(() => import("./pages/ChatPage")) },
  { path: "/reports", title: "Reports", feature: "reports_view", Page: lazyPage(() => import("./pages/ReportsPage")) },
  { path: "/reports/:id", title: "Report", feature: "reports_view", Page: lazyPage(() => import("./pages/ReportViewPage")) },
  { path: "/dashboard", title: "", showGreeting: true, Page: lazyPage(() => import("./pages/DashboardPage")) },
  { path: "/scheduled-dashboards", title: "Scheduled Dashboards", roles: ANALYST_ROLES, Page: lazyPage(() => import("./pages/ScheduledDashboardsPage")) },
  { path: "/custom-alerts", title: "Custom Alerts", roles: ANALYST_ROLES, Page: lazyPage(() => import("./pages/CustomAlertsPage")) },
  { path: "/status", title: "Service Status", feature: "service_status_view", Page: lazyPage(() => import("./pages/ServiceStatusPage")) },
  { path: "/status/incidents/:id", title: "Incident", feature: "service_status_view", Page: lazyPage(() => import("./pages/IncidentDetailPage")) },
  { path: "/status/incidents/:id/post-mortem", title: "Post-mortem", feature: "service_status_view", Page: lazyPage(() => import("./pages/IncidentPostmortemPage")) },
  { path: "/admin", title: "Admin", roles: ADMIN_ROLES, Page: lazyPage(() => import("./pages/AdminPage")) },
  { path: "/admin/users/invite", title: "Invite User", roles: ADMIN_ROLES, Page: lazyPage(() => import("./pages/AdminInviteUserPage")) },
  { path: "/admin/users/:userId", title: "Edit User", roles: ADMIN_ROLES, Page: lazyPage(() => import("./pages/AdminUserEditPage")) },
  { path: "/scheduled-actions", title: "Scheduled Actions", Page: lazyPage(() => import("./pages/ScheduledActionsPage")) },
  { path: "/changelog", title: "What's New", Page: lazyPage(() => import("./pages/ChangelogPage")) },
  { path: "/settings", title: "Settings", Page: lazyPage(() => import("./pages/SettingsPage")) },
];

const initialPath = window.location.pathname === "/" ? HOME_PATH : window.location.pathname;
matchRoutes(APP_ROUTES, initialPath)?.[0]?.route.Page.preload().catch(() => {});

function RoutePage({ route }: { route: AppRoute }) {
  const page = <route.Page />;
  if (route.feature) return <FeatureRoute feature={route.feature}>{page}</FeatureRoute>;
  if (route.roles) return <ProtectedRoute roles={route.roles}>{page}</ProtectedRoute>;
  return page;
}

function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, refreshUser, viewAsRole, setViewAsRole } = useAuth();
  const location = useLocation();
  const route = matchRoutes(APP_ROUTES, location)?.[0]?.route;
  const routePath = route?.path;
  const firstName = user?.full_name?.split(" ")[0] ?? user?.email?.split("@")[0] ?? "";
  const displayTitle = route?.showGreeting ? `${getGreeting()}${firstName ? `, ${firstName}` : ""}` : (route?.title ?? "");
  const navigate = useNavigate();
  const mainRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    mainRef.current?.scrollTo(0, 0);
  }, [routePath]);

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex flex-1 flex-col overflow-hidden">
        <TopBar title={displayTitle} onMenuClick={() => setSidebarOpen(true)} />
        {user && (
          <TwoFAGraceNotice
            user={user}
            onGoToSettings={() => navigate("/settings?tab=security")}
          />
        )}
        {IS_DEMO && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-1.5 text-center text-xs text-amber-800">
            Demo mode — all data is synthetic and does not represent real activity.
          </div>
        )}
        {viewAsRole && (
          <div className="flex items-center justify-center gap-3 border-b border-amber-300 bg-amber-100 px-4 py-1.5 text-center text-xs text-amber-900">
            <span>
              Previewing as <strong className="capitalize">{viewAsRole}</strong> — you're seeing what this role sees.
            </span>
            <button
              onClick={() => setViewAsRole(null)}
              className="font-semibold underline hover:text-amber-950"
            >
              Exit preview
            </button>
          </div>
        )}
        <main ref={mainRef} className="flex-1 overflow-auto">
          <Suspense fallback={<LoadingText className="p-6" />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {user && !user.onboarding_completed && (
        <OnboardingWizard
          agentName={user.agent_name ?? "Brian"}
          onComplete={refreshUser}
        />
      )}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
        <ErrorBoundary>
        <Routes>
          <Route path="/demo" element={<DemoLandingPage />} />
          <Route path="/login" element={IS_DEMO ? <Navigate to="/demo" replace /> : <LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            {APP_ROUTES.map((route) => (
              <Route key={route.path} path={route.path} element={<RoutePage route={route} />} />
            ))}
          </Route>
          <Route path="/" element={<Navigate to={HOME_PATH} replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </ErrorBoundary>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
